











import { Component, Prop, Vue } from 'vue-property-decorator';
import { IFootbowlState } from './types';
import FootbowlDisplay from './FootbowlDisplay.vue';
import gql from 'graphql-tag';

interface ISubscriptionResult {
  data: {
    footbowlState: IFootbowlState | null,
    footbowlPlay: null,
  } | null;
}

interface IGameResult {
  id: string;
  states: IFootbowlState[];
}

const gameStateFields = `{
  id
  sequenceNumber
  homeOnLeft
  possession
  down
  yardsToFirstDown
  homeScore
  visitorScore
  ballSide
  ballYardLine
  tiebreaker
  playComplete
  homeTeam
  visitorTeam
  frame
  homePosition
  visitorPosition
  yardsGained
  homeLane
  visitorLane
  homeBowlingFrame {
    frame
    squares
  }
  visitorBowlingFrame {
    frame
    squares
  }

  action {
    name
    description
    team
    hasDisplayTimeout
  }
}`;

const GameQuery = gql`
  query FootbowlGame($id: ID!) {
    footbowlGame(id: $id) {
      id
      states ${gameStateFields}
    }
  }
`;

const GameSubscription = gql`
  subscription GameSubscription($id: ID!, $fromSequenceNumber: Int) {
    footbowlState(gameId: $id, fromSequenceNumber: $fromSequenceNumber) ${
      gameStateFields
    }
  }
`;

const FindOrCreate = gql`
  mutation FindOrCreate($centerName: String, $lane: Int!) {
    findOrCreateGame(centerName: $centerName, lane: $lane, gameType: FOOTBOWL)
  }
`;

const ApolloOptions = Vue.extend({
  apollo: {
    footbowlGame: {
      query: GameQuery,
      subscribeToMore: {
        document: GameSubscription,
        updateQuery(prev: any, data: any) {
          return this.updateQuery(prev, data);
        },
        variables() {
          return {
            id: this.gameId,
            fromSequenceNumber: this.$route.query.fromSeqNumber || 0,
          };
        },
        skip() {
          return !(this as any).gameId || (this as FootbowlGame).skipSubscription;
        },
      },
      variables() {
        return {
          id: this.gameId,
        };
      },
      skip() {
        return !this.gameId;
      },
    },
  },
});

@Component<FootbowlGame>({
  components: {
    FootbowlDisplay,
  },
})
export default class FootbowlGame extends ApolloOptions {
  public footbowlGame: any = null;
  public lastState: IFootbowlState | null = null;
  public gameId: string | null = null;
  public skipSubscription = false;

  public centerName?: string;
  public lane?: string;

  public errorMessage: string | null = null;

  private isReplayGame = false;

  public created() {
    this.centerName = this.$route.params.center;
    this.lane = this.$route.params.lane;

    if (this.$route.params.gameId) {
      this.skipSubscription = true;
      this.isReplayGame = true;
      this.gameId = this.$route.params.gameId;
    } else {
      this.findOrCreate();
    }
  }

  public updateQuery(
    prev: { footbowlGame: IGameResult | null } | null,
    { subscriptionData }: { subscriptionData: ISubscriptionResult},
  ) {
    const { data } = subscriptionData;
    if (!data || !data.footbowlState) {
      // TODO: Replace me with a logger
      // tslint:disable-next-line:no-console
      console.warn('Data or state not returned by subscription result');
      return prev;
    }

    const obj = prev && prev.footbowlGame ? prev : { footbowlGame: { states: [] } };
    return this.addNewState(obj as any, data.footbowlState);
  }

  public onNextGame() {
    if (this.isReplayGame) {
      // tslint:disable-next-line:no-console
      console.debug('In replay mode, not going to start another game');
    } else {
      this.findOrCreate();
    }
  }

  private findOrCreate() {
    if (!this.centerName || !this.lane) {
      // tslint:disable-next-line:no-console
      console.error('A valid center name or lane was not provided');
      return;
    }

    this.$apollo.mutate({
      mutation: FindOrCreate,
      variables: {
        centerName: this.centerName,
        lane: this.lane,
      },
    }).then((result) => {
      const gameId = result && result.data && result.data.findOrCreateGame;
      if (!gameId) { return; }

      this.gameId = gameId;
    // tslint:disable-next-line:no-console
    }).catch((error) => console.error(error));
  }

  private addNewState(obj: { footbowlGame: IGameResult }, state: IFootbowlState) {
    this.lastState = state;

    if (obj.footbowlGame && obj.footbowlGame.states && obj.footbowlGame.states.some(
      (s) => s.id === state.id,
    )) {
      return obj;
    }

    return {
      ...obj,
      footbowlGame: {
        ...obj.footbowlGame,
        states: [
          ...obj.footbowlGame.states,
          Object.assign({}, obj.footbowlGame.states[0] || {}, state),
        ],
      },
    };
  }
}
